'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  validatePromoCode,
  isPromoCodeValid,
  type PromoCodeValidationResult,
} from '@/app/utils/checkout-api';
import { type PlanTier, formatPrice } from '@/app/config/plans';

export interface PromoCodeInputProps {
  /** The plan tier being purchased */
  planTier: PlanTier;
  /** Subscription length in years */
  years: number;
  /** Base price before discount */
  basePrice: number;
  /** Called when promo code is validated successfully or cleared */
  onValidated: (result: PromoCodeValidationResult | null, code: string | null) => void;
  /** Disable the input */
  disabled?: boolean;
  /** Initial promo code value (for restoring state) */
  initialCode?: string | null;
  /** Initial validation result (for restoring state) */
  initialResult?: PromoCodeValidationResult | null;
}

export default function PromoCodeInput({
  planTier,
  years,
  basePrice,
  onValidated,
  disabled = false,
  initialCode = null,
  initialResult = null,
}: PromoCodeInputProps) {
  const [isExpanded, setIsExpanded] = useState(!!initialCode);
  const [promoCode, setPromoCode] = useState(initialCode || '');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<PromoCodeValidationResult | null>(
    initialResult,
  );
  const [error, setError] = useState<string | null>(null);

  // Track previous years to detect changes
  const prevYearsRef = useRef(years);

  // Re-validate promo code when years changes (if we have a valid code)
  useEffect(() => {
    // Skip on initial mount
    if (prevYearsRef.current === years) {
      return;
    }
    prevYearsRef.current = years;

    // Only re-validate if we have a promo code entered
    const trimmedCode = promoCode.trim();
    if (!trimmedCode) {
      return;
    }

    // Re-validate the promo code with new years value
    const revalidate = async () => {
      setIsValidating(true);
      setError(null);

      try {
        const result = await validatePromoCode({
          promoCode: trimmedCode,
          tier: planTier,
          subscriptionLength: years,
          price: basePrice,
        });

        if (isPromoCodeValid(result)) {
          setValidationResult(result);
          setError(null);
          onValidated(result, trimmedCode);
        } else {
          setValidationResult(null);
          setError(result.error.message);
          onValidated(null, null);
        }
      } catch {
        setError('Unable to validate promo code. Please try again.');
        setValidationResult(null);
        onValidated(null, null);
      } finally {
        setIsValidating(false);
      }
    };

    revalidate();
  }, [years, promoCode, planTier, basePrice, onValidated]);

  // Handle input change - clear validation when user types
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // const value = e.target.value.toUpperCase();
      const value = e.target.value;
      setPromoCode(value);

      // Clear previous validation when user changes input
      if (validationResult || error) {
        setValidationResult(null);
        setError(null);
        onValidated(null, null);
      }
    },
    [validationResult, error, onValidated],
  );

  // Validate promo code
  const validateCode = useCallback(async () => {
    const trimmedCode = promoCode.trim();

    // Don't validate empty or unchanged codes
    if (!trimmedCode) {
      setError(null);
      setValidationResult(null);
      onValidated(null, null);
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await validatePromoCode({
        promoCode: trimmedCode,
        tier: planTier,
        subscriptionLength: years,
        price: basePrice,
      });

      if (isPromoCodeValid(result)) {
        setValidationResult(result);
        setError(null);
        onValidated(result, trimmedCode);
      } else {
        setValidationResult(null);
        setError(result.error.message);
        onValidated(null, null);
      }
    } catch {
      setError('Unable to validate promo code. Please try again.');
      setValidationResult(null);
      onValidated(null, null);
    } finally {
      setIsValidating(false);
    }
  }, [promoCode, planTier, years, basePrice, onValidated]);

  // Handle key down - validate on Enter
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        validateCode();
      }
    },
    [validateCode],
  );

  // Clear the promo code
  const handleClear = useCallback(() => {
    setPromoCode('');
    setValidationResult(null);
    setError(null);
    onValidated(null, null);
  }, [onValidated]);

  // Collapsed state - just show link
  if (!isExpanded) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="text-sm text-slate-600 hover:text-slate-700 cursor-pointer underline transition-colors"
        disabled={disabled}
      >
        Promo code
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <label htmlFor="promoCodeInput" className="block text-sm font-medium text-slate-600">
        Promo code
      </label>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            id="promoCodeInput"
            value={promoCode}
            onChange={handleChange}
            onBlur={validateCode}
            onKeyDown={handleKeyDown}
            placeholder="Enter code"
            disabled={disabled || isValidating}
            className={`form-input block w-full ${
              validationResult ? 'border-emerald-500 pr-10' : error ? 'border-red-500' : ''
            } ${isValidating ? 'opacity-50' : ''}`}
          />

          {/* Valid checkmark */}
          {validationResult && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <svg
                className="w-5 h-5 text-emerald-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}

          {/* Loading spinner */}
          {isValidating && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <svg className="animate-spin w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Clear button when code is entered */}
        {promoCode && (
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-slate-500 hover:text-slate-700 px-2 cursor-pointer"
            disabled={disabled || isValidating}
          >
            Clear
          </button>
        )}
      </div>

      {/* Success message with discount */}
      {validationResult && (
        <p className="text-sm text-emerald-600">
          {validationResult.percentageDiscount > 0
            ? `${validationResult.percentageDiscount}% off applied! You save ${formatPrice(validationResult.discount)}`
            : `Discount applied! You save ${formatPrice(validationResult.discount)}`}
        </p>
      )}

      {/* Error message */}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
